import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Grid, Paper, CircularProgress, Alert,
  Card, CardContent, Divider
} from '@mui/material';
import {
  Group as PeopleIcon,
  Assignment as TaskIcon,
  ShoppingCart as CartIcon,
  Support as SupportIcon
} from '@mui/icons-material';
import { dashboardService } from '../../services/dashboardService';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#E91E63', '#9C27B0', '#3F51B5'];

const StatCard = ({ title, value, icon: Icon, color }) => (
  <Card sx={{ height: '100%', borderTop: `4px solid ${color}` }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography color="text.secondary" gutterBottom variant="subtitle2">
            {title}
          </Typography>
          <Typography variant="h4" component="div">
            {value}
          </Typography>
        </Box>
        <Icon sx={{ fontSize: 40, color: color, opacity: 0.7 }} />
      </Box>
    </CardContent>
  </Card>
);

const ContactDashboard = () => {
  const { project } = useParams();
  const [data, setData] = useState(null);
  const [projectDetails, setProjectDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        // Fetch project details to check enabledFeatures
        const projData = await dashboardService.api.get(`/projects/${project}`);
        setProjectDetails(projData.data);

        const reportData = await dashboardService.getContactDashboardData(project);
        setData(reportData);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    if (project) {
      fetchDashboard();
    }
  }, [project]);

  const cityChartData = data?.cityDistribution || [];
  const countryChartData = data?.countryDistribution || [];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const features = projectDetails?.enabledFeatures || ['traffic'];
  const hasCRM = features.includes('crm');

  const topPagesData = data?.topPages || [];
  const trafficSourcesData = data?.trafficSources || [];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
        Dashboard Overview
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={hasCRM ? 4 : 12}>
          <StatCard
            title="Total Visitors"
            value={data?.anonymouscount || 0}
            icon={PeopleIcon}
            color="#2e7d32" // Success green
          />
        </Grid>
        {hasCRM && (
          <>
            <Grid item xs={12} sm={4}>
              <StatCard
                title="Total Contacts"
                value={data?.contactcount || 0}
                icon={PeopleIcon}
                color="#1976d2" // Primary blue
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                title="Total Activities"
                value={data?.activitycount || 0}
                icon={TaskIcon}
                color="#ed6c02" // Warning orange
              />
            </Grid>
          </>
        )}
      </Grid>

      <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 500 }}>Traffic Insights</Typography>

      {/* Traffic Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>City Insights</Typography>
            {cityChartData.length > 0 ? (
              <Box sx={{ height: 350, width: '100%', mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={cityChartData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {cityChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">No city traffic data available.</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>Country Insights</Typography>
            {countryChartData.length > 0 ? (
              <Box sx={{ height: 350, width: '100%', mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={countryChartData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {countryChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">No country traffic data available.</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Traffic Charts Row 2 */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>Top Pages</Typography>
            {topPagesData.length > 0 ? (
              <Box sx={{ height: 350, width: '100%', mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={topPagesData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label
                    >
                      {topPagesData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">No page view data available.</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>Traffic Sources</Typography>
            {trafficSourcesData.length > 0 ? (
              <Box sx={{ height: 350, width: '100%', mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={trafficSourcesData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label
                    >
                      {trafficSourcesData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">No traffic source data available.</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContactDashboard;
